import { useCallback, useMemo, useState } from 'react'

import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import UnsubscribeProfileModal from 'components/UI/Modal/profileModals/UnsubscribeProfileModal/UnsubscribeProfileModal'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import styles from 'features/NotificationCard/_NotificationCard.module.scss'
import { getLink } from 'helpers/appLinks'
import { toFixedNumber } from 'helpers/dataTransformations'
import { setSubscriptionRenewal } from 'services/endpoints/settings/general/subscription'

const SubscriptionExpiringNotifcation = ({ data }: { data: any }) => {
  const { t } = useTranslation()

  const [disableBtn, setDisableBtn] = useState(false)
  const [openRenewalModal, setOpenRenewalModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const subscriptionId = data?.subscription?.id

  const creator = useMemo(() => {
    const sender = data?.sender ?? {}
    return {
      avatar: sender?.avatar ?? sender?.avatar_url ?? sender?.profile_pic ?? null,
      displayName: sender?.name ?? sender?.display_name ?? '',
      username: sender?.username ?? sender?.handle ?? '',
    }
  }, [data])

/*   const expiryAt = useMemo(() => {
    return (
      data?.subscription?.expiry_at ??
      data?.subscription?.expires_at ??
      data?.subscription?.renewal_at ??
      null
    )
  }, [data]) */

  // HARDCODED TO TOMORROW
    const expiryAt = useMemo(() => {
        const d = new Date()
        d.setDate(d.getDate() + 1)
        // set to noon to avoid DST / midnight edge cases
        d.setHours(12, 0, 0, 0)
        return d.toISOString()
    }, [])

  const openConfirmModal = useCallback(() => {
    if (disableBtn) return
    setOpenRenewalModal(true)
  }, [disableBtn])

  const confirmEnableRenewal = useCallback(async () => {
    // prevent double submits if user spams button
    if (isSubmitting) return

    if (!subscriptionId) {
      addToast('error', t('error:renewalSwitchOnError'))
      return
    }

    try {
      setIsSubmitting(true)
      setDisableBtn(true)

      await setSubscriptionRenewal(subscriptionId)

      setOpenRenewalModal(false)
    } catch (error) {
      // IMPORTANT: re-enable so user can try again
      setDisableBtn(false)

      addToast('error', t('error:renewalSwitchOnError'))
      console.error('renewal error: ', error)
    } finally {
      setIsSubmitting(false)
    }
  }, [subscriptionId, t, isSubmitting])

  const getBySubscriptionStatus = useCallback(() => {
    if (data?.subscription_status === 'will_expire' || data?.isToast) {
      return (
        <>
          <div className={styles.btnsRow}>
            <Button
              text={
                <p>
                  <Trans i18nKey={'notifications:enableRenewal'}>
                    subscribe
                    <span className={styles.contentText}>
                      {{
                        price: toFixedNumber(data?.subscription?.price),
                      } as any}
                    </span>
                    mo
                  </Trans>
                </p>
              }
              customClass="notificationPriceButton"
              width="fit"
              color="blue"
              padding="1-15"
              disabled={disableBtn}
              clickFn={openConfirmModal}
            />
          </div>

          <ModalWrapper
            open={openRenewalModal}
            setOpen={setOpenRenewalModal}
            backgroundClickClose={!isSubmitting} // optional: don't allow closing mid-request
            hasCloseButton={false}               // optional: keep it consistent with your modal UI
          >
            <UnsubscribeProfileModal
              avatar={creator.avatar}
              displayName={creator.displayName}
              username={creator.username}
              setOpen={setOpenRenewalModal}
              rebill={0} // forces the "Renew Subscription" UI
              expiry_at={expiryAt}
              subscribeFn={confirmEnableRenewal}
              isLoading={isSubmitting}
            />
          </ModalWrapper>
        </>
      )
    }

    if (data?.subscription_status === 'renewed') {
      return (
        <>
          <br />
          {t('notifications:subscriptionWasRenewed')}
        </>
      )
    }

    if (data?.subscription_status === 'expired') {
      return (
        <>
          <br />
          {t('notifications:subscriptionHasExpired')}
        </>
      )
    }

    return null
  }, [
    data,
    disableBtn,
    openConfirmModal,
    openRenewalModal,
    isSubmitting,
    creator.avatar,
    creator.displayName,
    creator.username,
    expiryAt,
    confirmEnableRenewal,
    t,
  ])

  return (
    <>
      <Trans i18nKey={'notifications:subscriptionExpiring'}>
        subscription
        <Link to={getLink.profile(data?.sender?.id)}>
          {{ name: data?.sender?.name } as any}
        </Link>
        expired
      </Trans>

      {getBySubscriptionStatus()}
    </>
  )
}

export default SubscriptionExpiringNotifcation